import { platformLabel } from "@/lib/platformLabels";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Instagram, Facebook, Youtube, Linkedin, Globe, Phone, Mail,
  MapPin, CreditCard, Share2, Eye, CheckCircle, QrCode,
  Smartphone, Monitor, GripVertical, Info, RotateCcw
} from "lucide-react";
import { getThemeById } from "@commonqr/shared/config/themes";
import { WhatsAppIcon, XIcon, TikTokIcon, TelegramIcon, SnapchatIcon, BRAND_COLORS } from "@/components/icons/BrandIcons";

const PLATFORM_STYLES: Record<string, { icon: any; style: string; color: string }> = {
  instagram: { icon: Instagram, style: "bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600", color: "text-white" },
  whatsapp: { icon: WhatsAppIcon, style: "bg-[#25D366]", color: "text-white" },
  facebook: { icon: Facebook, style: "bg-[#1877F2]", color: "text-white" },
  twitter: { icon: XIcon, style: "bg-black", color: "text-white" },
  linkedin: { icon: Linkedin, style: "bg-[#0A66C2]", color: "text-white" },
  youtube: { icon: Youtube, style: "bg-[#FF0000]", color: "text-white" },
  tiktok: { icon: TikTokIcon, style: "bg-black", color: "text-white" },
  email: { icon: Mail, style: "bg-blue-500", color: "text-white" },
  phone: { icon: Phone, style: "bg-green-600", color: "text-white" },
  website: { icon: Globe, style: "bg-slate-700", color: "text-white" },
  location: { icon: MapPin, style: "bg-red-500", color: "text-white" },
  payment: { icon: CreditCard, style: "bg-indigo-500", color: "text-white" },
  telegram: { icon: TelegramIcon, style: "bg-[#2CA5E0]", color: "text-white" },
  snapchat: { icon: SnapchatIcon, style: "bg-yellow-400", color: "text-black" },
  custom: { icon: Globe, style: "bg-slate-600", color: "text-white" },
};

export interface PreviewLink {
  id: number | string;
  platform: string;
  label: string;
  url: string;
  sortOrder?: number;
}

export interface PreviewProfile {
  name: string;
  bio?: string;
  location?: string;
  avatarUrl?: string;
  username: string;
  isVerified?: boolean;
  scanCount?: number;
  coverFrom?: string;
  coverTo?: string;
  themeId?: string | null;
  themeImageUrl?: string | null;
  linkLayout?: string;
  iconColor?: string;
}

interface ProfilePreviewProps {
  profile: PreviewProfile;
  links: PreviewLink[];
  allowReorder?: boolean;
  onReorder?: (links: PreviewLink[]) => void;
  isDemo?: boolean;
  hideHeader?: boolean;
  viewMode?: "mobile" | "desktop";
}

// Icons layout: at most 4 icons per row, rows balanced (5 → 3+2, 6 → 3+3, 7 → 4+3), last row centred.
function iconRowWidth(count: number, size: number, gap = 12) {
  const rows = Math.max(1, Math.ceil(count / 4));
  const perRow = Math.ceil(count / rows);
  return perRow * size + (perRow - 1) * gap;
}

function PhoneFrame({ children, hasTheme }: { children: React.ReactNode; hasTheme?: boolean }) {
  return (
    <div className="relative overflow-hidden rounded-[48px] shadow-[0_8px_40px_rgba(0,0,0,0.3)]" style={{ width: 340, height: 736, background: hasTheme ? "transparent" : "white" }}>
      <div className="overflow-y-auto h-full scrollbar-hide">
        {children}
      </div>
    </div>
  );
}

function DesktopFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto" style={{ maxWidth: 460 }}>
      <div className="bg-muted border border-border rounded-t-xl px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 bg-card border border-border rounded-md px-3 py-1 text-xs text-muted-foreground font-mono truncate">
          commonqr.com/demo
        </div>
      </div>
      <div className="bg-muted border border-t-0 border-border rounded-b-xl overflow-hidden" style={{ height: 560 }}>
        <div className="overflow-y-auto h-full">
          {children}
        </div>
      </div>
    </div>
  );
}

function ProfileCard({
  profile,
  links,
  allowReorder,
  onReorder,
}: {
  profile: PreviewProfile;
  links: PreviewLink[];
  allowReorder?: boolean;
  onReorder?: (links: PreviewLink[]) => void;
}) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [localLinks, setLocalLinks] = useState(links);
  const currentLinks = allowReorder ? localLinks : links;

  const handleDragStart = (i: number) => setDragIdx(i);
  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    const newList = [...localLinks];
    newList.splice(i, 0, newList.splice(dragIdx, 1)[0]);
    setLocalLinks(newList);
    setDragIdx(i);
  };
  const handleDrop = () => {
    setDragIdx(null);
    if (onReorder) onReorder(localLinks);
  };

  const coverFrom = profile.coverFrom || "#6366f1";
  const coverTo = profile.coverTo || "#9333ea";
  const selectedTheme = getThemeById(profile.themeId);
  const headerImageUrl = profile.themeId ? `/api/v1/themes/image/${profile.themeId}` : null;
  const hasTheme = !!headerImageUrl;

  const headerStyle = hasTheme
    ? { backgroundImage: `url("${headerImageUrl}")`, backgroundPosition: "center", backgroundSize: "cover" }
    : { background: `linear-gradient(to right, ${coverFrom}, ${coverTo})` };

  // CSS variable-driven token helpers (active when theme is applied)
  const tokenText  = "var(--cqr-color-text, #ffffff)";
  const tokenMuted = "var(--cqr-color-muted, rgba(255,255,255,0.7))";
  const tokenBtn   = "var(--cqr-color-button, #8b5cf6)";
  const tokenBtnTx = "var(--cqr-color-button-text, #ffffff)";
  const tokenBorder = "var(--cqr-color-border, rgba(255,255,255,0.25))";
  const tokenOverlay = "var(--cqr-color-overlay, rgba(0,0,0,0.20))";
  const tokenRadius  = "var(--cqr-radius-button, 9999px)";
  const tokenShadow  = "var(--cqr-shadow-button, none)";
  const tokenBlur    = "var(--cqr-blur, 10px)";
  const tokenOpacity = "var(--cqr-opacity, 0.10)";
  const tokenSizeName   = "var(--cqr-size-name, 18px)";
  const tokenSizeBtn    = "var(--cqr-size-button, 13px)";
  const tokenSizeMeta   = "var(--cqr-size-meta, 11px)";
  const tokenWeightHead = "var(--cqr-weight-heading, 800)";
  const tokenWeightBtn  = "var(--cqr-weight-button, 600)";
  const tokenFontHead   = "var(--cqr-font-heading, inherit)";
  const tokenFontBody   = "var(--cqr-font-body, inherit)";
  const tokenFontBtn    = "var(--cqr-font-button, inherit)";

  // Icon colour: "brand" tints each platform icon in its official colour (themed profiles only).
  const iconTint = (platform: string) =>
    profile.iconColor === "brand" && BRAND_COLORS[platform] ? { color: BRAND_COLORS[platform] } : undefined;

  return (
    <div className="min-h-full relative" style={{ fontFamily: tokenFontBody }}>
      {/* Cover */}
      <div className={hasTheme ? "absolute inset-0" : "h-40 relative"} style={headerStyle}>
        {hasTheme ? (
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 35%, rgba(0,0,0,0) 65%), ${tokenOverlay}` }} />
        ) : (
          <div className="absolute inset-0 opacity-20 mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" />
        )}
        <div className="absolute top-3 right-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-md" style={{ background: "rgba(255,255,255,0.20)", color: "#ffffff" }}>
            <Share2 className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Profile header */}
      <div className={hasTheme ? "px-5 pb-5 relative text-center pt-8 z-10" : "px-5 pb-5 relative text-center"}>
        {/* Avatar */}
        <div className={hasTheme
          ? "w-20 h-20 mx-auto rounded-full border-4 overflow-hidden shadow-lg relative flex items-center justify-center shrink-0 backdrop-blur-sm"
          : "w-20 h-20 mx-auto -mt-10 rounded-full border-4 border-white bg-indigo-100 overflow-hidden shadow-lg relative flex items-center justify-center shrink-0"}
          style={hasTheme ? { borderColor: "rgba(255,255,255,0.50)", background: "rgba(255,255,255,0.20)" } : undefined}
        >
          {profile.avatarUrl ? (
            <img src={profile.avatarUrl} alt={profile.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl font-bold uppercase" style={hasTheme ? { color: tokenText } : { color: "#4f46e5" }}>
              {(profile.name ?? "U").charAt(0)}
            </span>
          )}
        </div>

        {/* Name */}
        <h1 className="mt-3 flex justify-center items-center gap-1"
          style={{
            color: hasTheme ? tokenText : "#0f172a",
            fontSize: hasTheme ? tokenSizeName : "18px",
            fontWeight: hasTheme ? tokenWeightHead : 800,
            fontFamily: hasTheme ? tokenFontHead : "inherit",
          }}
        >
          {profile.name}
          {profile.isVerified && <CheckCircle className="w-4 h-4 text-blue-400 fill-blue-400/20 shrink-0" />}
        </h1>

        {/* Meta chips */}
        <div className="flex items-center justify-center gap-2 mt-1.5 flex-wrap">
          {profile.location && (
            <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-md border"
              style={hasTheme
                ? { color: tokenMuted, borderColor: tokenBorder, background: "rgba(255,255,255,0.08)", fontSize: tokenSizeMeta }
                : { color: "#64748b", borderColor: "#e2e8f0", background: "#f8fafc", fontSize: "11px" }}
            >
              <MapPin className="w-3 h-3" /> {profile.location}
            </span>
          )}
          <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-md border"
            style={hasTheme
              ? { color: tokenMuted, borderColor: tokenBorder, background: "rgba(255,255,255,0.08)", fontSize: tokenSizeMeta }
              : { color: "#64748b", borderColor: "#e2e8f0", background: "#f8fafc", fontSize: "11px" }}
          >
            <Eye className="w-3 h-3" /> {(profile.scanCount || 0).toLocaleString()} Scans
          </span>
        </div>

        {/* Bio */}
        {profile.bio && (
          <p className="mt-3 leading-relaxed px-1 line-clamp-3"
            style={hasTheme
              ? { color: tokenMuted, fontSize: "13px", fontFamily: tokenFontBody }
              : { color: "#475569", fontSize: "13px" }}
          >
            {profile.bio}
          </p>
        )}
      </div>

      {/* Links */}
      <div className={hasTheme ? "px-4 pb-6 relative z-10" : "px-4 pb-6"}>
        {allowReorder && (
          <p className="text-center text-xs text-muted-foreground mb-2 flex items-center justify-center gap-1">
            <GripVertical className="w-3.5 h-3.5" /> Drag links to reorder
          </p>
        )}
                {profile.linkLayout === 'icons' ? (
          <div className="flex flex-wrap justify-center gap-3 mx-auto" style={{ maxWidth: iconRowWidth(currentLinks.length, 48) }}>
            {currentLinks.map((link, index) => {
              const def = PLATFORM_STYLES[link.platform] || PLATFORM_STYLES.custom;
              const Icon = def.icon;
              return (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.04 }}
                  title={link.label || platformLabel(link.platform)}
                  className="flex items-center justify-center w-12 h-12 rounded-full cursor-pointer hover:scale-110 transition-all"
                  style={{
                    background: `color-mix(in srgb, ${tokenBtn} calc(${tokenOpacity} * 100%), transparent)`,
                    border: `1px solid ${tokenBorder}`,
                    color: tokenBtnTx,
                    backdropFilter: `blur(${tokenBlur})`,
                  }}
                >
                  <Icon className="w-5 h-5" style={iconTint(link.platform)} />
                </motion.div>
              );
            })}
          </div>
        ) : profile.linkLayout === 'compact' ? (
          <div className="flex flex-col gap-1.5">
            {currentLinks.map((link, index) => {
              const def = PLATFORM_STYLES[link.platform] || PLATFORM_STYLES.custom;
              const Icon = def.icon;
              return (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04 }}
                  className="flex items-center justify-center gap-2 h-9 px-4 mx-6 cursor-pointer hover:scale-[1.02] transition-all"
                  style={{
                    borderRadius: tokenRadius,
                    border: `1px solid ${tokenBorder}`,
                    background: `color-mix(in srgb, ${tokenBtn} calc(${tokenOpacity} * 100%), transparent)`,
                    color: tokenBtnTx,
                    fontSize: tokenSizeBtn,
                    fontFamily: tokenFontBtn,
                    backdropFilter: `blur(${tokenBlur})`,
                  }}
                >
                  <Icon className="w-4 h-4 shrink-0" style={iconTint(link.platform)} />
                  <span>{link.label || platformLabel(link.platform)}</span>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {currentLinks.map((link, index) => {
              const def = PLATFORM_STYLES[link.platform] || PLATFORM_STYLES.custom;
              const Icon = def.icon;
              if (hasTheme) {
                return (
                  <motion.div
                    key={link.id}
                    draggable={allowReorder}
                    onDragStart={() => allowReorder && handleDragStart(index)}
                    onDragOver={(e: React.DragEvent) => allowReorder && handleDragOver(e, index)}
                    onDrop={() => allowReorder && handleDrop()}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                    className={`flex items-center justify-center gap-3 h-12 w-full px-4 transition-all ${dragIdx === index ? "opacity-50 scale-95" : ""}`}
                    style={{
                      borderRadius: tokenRadius,
                      border: `1px solid ${tokenBorder}`,
                      background: `color-mix(in srgb, ${tokenBtn} calc(${tokenOpacity} * 100%), transparent)`,
                      color: tokenBtnTx,
                      fontSize: tokenSizeBtn,
                      fontWeight: tokenWeightBtn,
                      fontFamily: tokenFontBtn,
                      boxShadow: tokenShadow,
                      backdropFilter: `blur(${tokenBlur})`,
                      WebkitBackdropFilter: `blur(${tokenBlur})`,
                    }}
                  >
                    {allowReorder && <GripVertical className="w-4 h-4 opacity-60 shrink-0" />}
                    <Icon className="w-5 h-5 shrink-0" style={iconTint(link.platform)} />
                    <span>{link.label || platformLabel(link.platform)}</span>
                  </motion.div>
                );
              }
              return (
                <motion.div
                  key={link.id}
                  draggable={allowReorder}
                  onDragStart={() => allowReorder && handleDragStart(index)}
                  onDragOver={(e: React.DragEvent) => allowReorder && handleDragOver(e, index)}
                  onDrop={() => allowReorder && handleDrop()}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04 }}
                  className={`flex items-center gap-3 p-3 rounded-xl border border-transparent transition-all ${allowReorder ? "cursor-grab active:cursor-grabbing" : "cursor-pointer hover:scale-[1.02]"} ${dragIdx === index ? "opacity-50 scale-95" : ""} ${def.style} ${def.color} shadow-sm`}
                >
                  {allowReorder && <GripVertical className="w-4 h-4 opacity-60 shrink-0" />}
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className="font-semibold text-sm">{link.label || platformLabel(link.platform)}</span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="py-4 text-center bg-muted border-t border-border mt-auto">
        <a href="/" className="inline-flex flex-col items-center gap-0.5 group">
          <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">Powered by</span>
          <span className="font-bold text-foreground group-hover:text-indigo-600 transition-colors flex items-center gap-1 text-sm">
            <QrCode className="w-4 h-4 text-indigo-600" /> CommonQR
          </span>
        </a>
      </div>
    </div>
  );
}

export default function ProfilePreview({
  profile,
  links,
  allowReorder = false,
  onReorder,
  isDemo = false,
  hideHeader = false,
  viewMode: externalViewMode,
}: ProfilePreviewProps) {
  const [internalViewMode, setViewMode] = useState<"mobile" | "desktop">("mobile");
  const viewMode = externalViewMode ?? internalViewMode;
  const [localLinks, setLocalLinks] = useState(links);

  const handleReorder = (newLinks: PreviewLink[]) => {
    setLocalLinks(newLinks);
    if (onReorder) onReorder(newLinks);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        {!hideHeader && (
          <div>
            <h2 className="text-[13px] font-semibold">Profile Preview</h2>
            <p className="text-muted-foreground text-[11px] mt-0.5">
              {allowReorder ? "Drag to reorder links." : "Live preview of your public profile."}
            </p>
          </div>
        )}
        <div className={hideHeader ? "hidden" : "flex items-center bg-muted rounded-xl p-1 border border-border gap-1 ml-auto"}>
          <button onClick={() => setViewMode("mobile")} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === "mobile" ? "bg-card shadow-sm text-primary border border-border" : "text-muted-foreground hover:text-foreground"}`}>
            <Smartphone className="w-4 h-4" /> Mobile
          </button>
          <button onClick={() => setViewMode("desktop")} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === "desktop" ? "bg-card shadow-sm text-primary border border-border" : "text-muted-foreground hover:text-foreground"}`}>
            <Monitor className="w-4 h-4" /> Desktop
          </button>
        </div>
      </div>

      {allowReorder && (
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700 mb-6">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />
          <div>
            <strong>How to reorder:</strong> Drag any link button up or down to change the order. The first link gets the most clicks — put your most important action first.
            <button onClick={() => setLocalLinks(links)} className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:underline font-medium">
              <RotateCcw className="w-3.5 h-3.5" /> Reset order
            </button>
          </div>
        </div>
      )}

      {isDemo && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-sm text-amber-700 mb-6">
          <Info className="w-4 h-4 shrink-0 text-amber-500" />
          This is a demo preview. Create your account to build your own profile.
        </div>
      )}

      <div className="flex justify-center py-4">
        <AnimatePresence mode="wait">
          {viewMode === "mobile" ? (
            <motion.div key="mobile" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="w-full">
              <PhoneFrame hasTheme={!!(profile.themeImageUrl || profile.themeId)}>
                <ProfileCard profile={profile} links={allowReorder ? localLinks : links} allowReorder={allowReorder} onReorder={handleReorder} />
              </PhoneFrame>
            </motion.div>
          ) : (
            <motion.div key="desktop" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="w-full max-w-xl">
              <DesktopFrame>
                <ProfileCard profile={profile} links={allowReorder ? localLinks : links} allowReorder={allowReorder} onReorder={handleReorder} />
              </DesktopFrame>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
